import { Text, Row, Box, Button, Modal, Skeleton, Pressable } from 'native-base'
import React, { Fragment, useRef } from 'react'
import { StyleSheet, ScrollView, Platform } from 'react-native'
import { SafeAreaView } from 'react-native-safe-area-context'

import {
  Icon,
  LogoWithGradient,
  ScreenHeaderWithPoints,
  Spacer,
  UpdatePointsRewardInfo,
  Version,
} from '~components'
import { COINS_REWARD_VALUE, RFColors, userDetailsFormWidth, WEB } from '~constants'
import { dispatchType } from '~filtersReducer'
import {
  useAuth,
  useCallback,
  useEffect,
  useFilters,
  useState,
  useToast,
  useTranslation,
} from '~hooks'
import { useGetCountriesList, useGetUser, useGetInterestsList } from '~query-hooks'
import { Routes } from '~types/routesNames'
import { getCountryNameByIso2, noop } from '~utils'

const HIT_SLOP = { top: 10, bottom: 10, left: 10, right: 10 }

export const UserProfileScreen = (props: UserProfileScreenProps): JSX.Element => {
  const {
    route: { params } = {},
    navigation: { navigate },
  } = props

  const { profileUpdated, signOut } = useAuth()
  const { filtersDispatch } = useFilters()
  const { showToast } = useToast()
  const { t } = useTranslation()
  const { isLoadingUser, user } = useGetUser()
  const { showRewardModal } = params || {}
  const { email, basicInfo } = user || {}
  const {
    birthDate,
    gender,
    location,
    countryId,
    stateId,
    state,
    cityId,
    city,
    zipCode,
    interests,
  } = basicInfo || {}

  const [showModal, setShowModal] = useState(false)
  const enableToast = useRef(false)
  const { countries: locationList } = useGetCountriesList()
  const { interests: interestsList } = useGetInterestsList()

  const onEditUserDetailsPress = useCallback(() => {
    enableToast.current = true
    navigate(Routes.UserDetailsForm, { showRewardInfo: !profileUpdated })
  }, [navigate, profileUpdated])

  const handleModalClose = useCallback(() => setShowModal(false), [setShowModal])

  const handleSignOut = useCallback(() => {
    signOut()
    filtersDispatch({ type: dispatchType.RESET_FILTERS })
  }, [filtersDispatch, signOut])

  const getInterestNamesByIds = useCallback(
    (interestIds: string[]) => {
      if (!interestsList || !interestIds) return []
      return interestIds
        .map((id) => interestsList.find((interest) => interest.value === id)?.label)
        .filter(Boolean)
    },
    [interestsList]
  )

  const getBirthYear = useCallback((birthDate?: string | number | Date) => {
    if (!birthDate) return undefined
    if (typeof birthDate === 'number') return birthDate.toString()
    if (typeof birthDate === 'string') {
      // If it's a string, try to extract year
      const year = parseInt(birthDate, 10)
      if (!isNaN(year) && year > 1900 && year < 2100) return year.toString()
      // If it's a date string, try to parse it
      const date = new Date(birthDate)
      if (!isNaN(date.getTime())) return date.getFullYear().toString()
      return birthDate
    }
    if (birthDate instanceof Date) {
      return birthDate.getFullYear().toString()
    }
    return undefined
  }, [])

  const UserInformation = useCallback(() => {
    const interestNames = getInterestNamesByIds(interests || [])
    const birthYear = getBirthYear(birthDate)
    const dataToDisplay = [
      {
        label: t('common.email'),
        value: email,
      },
      {
        label: t('common.birth_date'),
        value: birthYear,
      },
      {
        label: t('common.gender'),
        value: gender,
      },
      {
        label: t('common.country'),
        value: getCountryNameByIso2((countryId || location || '').toString(), locationList || []),
      },
      {
        label: t('common.state'),
        value: state || stateId,
      },
      {
        label: t('common.city'),
        value: city || cityId,
      },
      {
        label: t('common.zip_code'),
        value: zipCode,
      },
      {
        label: t('common.interests'),
        value: interestNames.join(', ') || '',
      },
    ]
    return (
      <>
        {dataToDisplay.map((el, index) => (
          <Fragment key={index}>
            <Text fontSize="sm" {...(index && { mt: '5' })}>
              {el.label}
            </Text>
            <Text fontSize="md" opacity={el?.value ? 1 : 0.4} fontWeight="500">
              {el?.value || t('user_profile_screen.no_info')}
            </Text>
          </Fragment>
        ))}
      </>
    )
  }, [
    birthDate,
    email,
    gender,
    location,
    countryId,
    state,
    stateId,
    city,
    cityId,
    zipCode,
    interests,
    locationList,
    t,
    getInterestNamesByIds,
    getBirthYear,
  ])

  useEffect(() => {
    setShowModal(!!showRewardModal)
  }, [showRewardModal])

  useEffect(() => {
    // if  showRewardModal === false it means that userData was updated, if is undefined do not show toast
    showRewardModal === false &&
      enableToast.current &&
      showToast({ description: t('toast_messages.user_updated_successfully'), variant: 'success' })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- showToast as deps create infinitive loop
  }, [birthDate, t, showRewardModal, gender, location])

  return isLoadingUser ? (
    <LoadingUserComponent />
  ) : (
    <SafeAreaView style={styles.safeArea}>
      <Box flex={1}>
        <ScreenHeaderWithPoints title={t('user_profile_screen.profile')} />
        <ScrollView style={styles.scrollContainer} contentContainerStyle={styles.scrollView}>
          <Box style={styles.contentWrapper}>
            <Box {...(!profileUpdated && { mb: '2' })}>
              {!profileUpdated && <UpdatePointsRewardInfo />}
              <Box w="full" rounded="lg" bgColor="white" p="4" mt="4">
                <Row justifyContent="space-between" mb="6">
                  <Text fontSize="lg" bold>
                    {t('user_profile_screen.personal_info')}
                  </Text>
                  <Pressable
                    onPress={onEditUserDetailsPress}
                    flexDirection="row"
                    hitSlop={HIT_SLOP}
                    alignItems="center"
                  >
                    <Icon name="pencil-line" size={16} color="actionBlue" />
                    <Text fontSize="md" color="actionBlue" ml="1" mr="3">
                      {t('common.edit')}
                    </Text>
                  </Pressable>
                </Row>
                <UserInformation />
              </Box>
              <Spacer y="6" />
              <Button onPress={handleSignOut} variant="ghost">
                <Text textAlign="center" color="actionBlue" fontSize="md" bold>
                  {t('user_profile_screen.log_out')}
                </Text>
              </Button>
            </Box>
            <Modal isOpen={showModal}>
              <Modal.Content w="90%" mx="6">
                <Box pt="12" px="6" pb="6">
                  <Row alignItems="center" justifyContent="center" mb="8">
                    <Text fontFamily="Roboto-Bold" fontSize="5xl" color="actionBlue" mr="3">
                      {COINS_REWARD_VALUE}
                    </Text>
                    <LogoWithGradient size="8" />
                  </Row>
                  <Text textAlign="center" bold fontSize="xl" mb="4">
                    {t('user_profile_screen.reward_modal_title')}
                  </Text>
                  <Text textAlign="center" fontSize="sm" color="offMain" mb="10">
                    {t('user_profile_screen.reward_modal_description')}
                  </Text>
                  <Button bgColor="offBlue" onPress={handleModalClose}>
                    <Text color="actionBlue" bold fontSize="md">
                      {t('user_profile_screen.reward_modal_button')}
                    </Text>
                  </Button>
                </Box>
              </Modal.Content>
            </Modal>
            <Version onPress={noop} />
          </Box>
        </ScrollView>
      </Box>
    </SafeAreaView>
  )
}

const styles = StyleSheet.create({
  contentWrapper: {
    flex: 1,
    ...(Platform.OS === WEB && {
      maxWidth: userDetailsFormWidth,
      minWidth: '100%',
      alignSelf: 'center',
      width: '100%',
    }),
  },
  safeArea: {
    backgroundColor: RFColors['background'],
    flex: 1,
    paddingTop: 32,
  },
  scrollContainer: {
    flex: 1,
  },
  scrollView: {
    flexGrow: 1,
    justifyContent: 'space-between',
    paddingBottom: 16,
    paddingHorizontal: 16,
  },
})

const LoadingUserComponent = () => {
  return (
    <Row justifyContent="space-between" alignItems="flex-end" px="6" pb="4">
      <Skeleton.Text fontSize="xl" lines={1} py="4" width="80%" />
      <Skeleton.Text fontSize="xl" lines={1} py="4" width="10%" />
    </Row>
  )
}
